import { useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Plus, Pencil, Trash2, FolderKanban } from "lucide-react";
import { toast } from "sonner";

import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Card, CardContent } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Progress } from "@/components/ui/progress";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle,
} from "@/components/ui/alert-dialog";

import { BarraFiltros } from "@/components/BarraFiltros";
import { PessoaSearchCombobox } from "@/components/PessoaSearchCombobox";
import { UsuarioSearchCombobox } from "@/components/UsuarioSearchCombobox";
import { PaginacaoRodape } from "@/components/PaginacaoRodape";
import { SortableTableHead } from "@/components/SortableTableHead";
import { usePaginacaoLocal } from "@/hooks/usePaginacaoLocal";
import { useOrdenacaoLocal } from "@/hooks/useOrdenacaoLocal";
import { useNomesUsuarios } from "@/hooks/useNomesUsuarios";
import { rotuloCodigoNome } from "@/lib/consultas";
import { useProjetos, useProjetoMutations } from "@/features/projetos/hooks/useProjetos";
import { STATUS_PROJETO, type FiltrosProjetos, type ProjetoResumo } from "@/features/projetos/types";

const STATUS_CLASSE: Record<number, string> = {
  0: "bg-slate-100 text-slate-700",
  1: "bg-blue-100 text-blue-700",
  2: "bg-amber-100 text-amber-700",
  3: "bg-green-100 text-green-700",
  4: "bg-red-100 text-red-700",
};

const fmtData = (d: string | null) => (d ? d.slice(0, 10).split("-").reverse().join("/") : "—");
const fmtMoeda = (v: number) =>
  v.toLocaleString("pt-BR", { style: "currency", currency: "BRL" });

export default function Projetos() {
  const navigate = useNavigate();
  // Filtros no servidor (status/cliente/gerente/busca por projeto ou cliente).
  const [filtros, setFiltros] = useState<FiltrosProjetos>({});
  const { data, isLoading } = useProjetos(filtros);
  const projetos = useMemo(() => data ?? [], [data]);
  const { excluir } = useProjetoMutations();
  const { nome: nomeUsuario } = useNomesUsuarios(projetos.map(p => p.gerenteId));

  const [excluirId, setExcluirId] = useState<number | null>(null);

  const filtrosAtivos = [filtros.status != null, !!filtros.clienteId, !!filtros.gerenteId, !!filtros.busca]
    .filter(Boolean).length;

  type Campo = "nome" | "cliente" | "status" | "progresso" | "inicio" | "fim" | "valor";
  const { itensOrdenados, ordenarPor, direcao, onOrdenar } = useOrdenacaoLocal<ProjetoResumo, Campo>(
    projetos,
    {
      nome: p => p.nome,
      cliente: p => p.clienteNome,
      status: p => p.status,
      progresso: p => Number(p.percentualConcluido),
      inicio: p => p.dtInicioPlanejada,
      fim: p => p.dtFimPlanejada,
      valor: p => Number(p.valorOrcado),
    },
    { ordenarPor: "inicio", direcao: "desc" },
  );
  const pag = usePaginacaoLocal(itensOrdenados);
  const ord = { ordenarPor, direcao, onOrdenar };

  const confirmarExclusao = async () => {
    if (excluirId == null) return;
    try {
      await excluir.mutateAsync(excluirId);
      toast.success("Projeto excluído.");
    } catch (e) {
      toast.error("Não foi possível excluir o projeto.");
    } finally {
      setExcluirId(null);
    }
  };

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="flex items-center gap-2 text-2xl font-bold">
            <FolderKanban className="h-6 w-6" /> Projetos
          </h1>
          <p className="text-sm text-muted-foreground">Gestão de projetos, fases, tarefas e dependências.</p>
        </div>
        <Button onClick={() => navigate("/operacional/projetos/novo")}>
          <Plus className="mr-2 h-4 w-4" /> Novo Projeto
        </Button>
      </div>

      <Card>
        <CardContent className="pt-6">
          <BarraFiltros
            className="mb-4"
            busca={filtros.busca ?? ""}
            onBuscar={b => setFiltros(f => ({ ...f, busca: b || undefined }))}
            placeholderBusca="Projeto ou cliente..."
            filtrosAtivos={filtrosAtivos}
            onLimpar={() => setFiltros({})}
          >
            <div className="space-y-1">
              <Label className="text-xs text-muted-foreground">Status</Label>
              <Select
                value={filtros.status != null ? String(filtros.status) : "__todos__"}
                onValueChange={v => setFiltros(f => ({ ...f, status: v === "__todos__" ? undefined : Number(v) }))}
              >
                <SelectTrigger className="h-9 w-[160px]"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="__todos__">Todos</SelectItem>
                  {STATUS_PROJETO.map((s, i) => <SelectItem key={i} value={String(i)}>{s}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            <div className="w-[260px] space-y-1">
              <Label className="text-xs text-muted-foreground">Cliente</Label>
              <PessoaSearchCombobox
                value={filtros.clienteId}
                onChange={id => setFiltros(f => ({ ...f, clienteId: id || undefined }))}
                filter="cliente"
                placeholder="Todos os clientes"
              />
            </div>
            <div className="w-[240px] space-y-1">
              <Label className="text-xs text-muted-foreground">Gerente</Label>
              <UsuarioSearchCombobox
                value={filtros.gerenteId}
                onChange={id => setFiltros(f => ({ ...f, gerenteId: id || undefined }))}
                placeholder="Todos os gerentes"
              />
            </div>
          </BarraFiltros>
          <Table>
            <TableHeader>
              <TableRow>
                <SortableTableHead campo="nome" {...ord}>Nome</SortableTableHead>
                <SortableTableHead campo="cliente" {...ord}>Cliente</SortableTableHead>
                <TableHead>Gerente</TableHead>
                <SortableTableHead campo="status" {...ord}>Status</SortableTableHead>
                <SortableTableHead campo="progresso" {...ord} className="w-[180px]">Progresso</SortableTableHead>
                <SortableTableHead campo="inicio" {...ord}>Início</SortableTableHead>
                <SortableTableHead campo="fim" {...ord}>Fim</SortableTableHead>
                <SortableTableHead campo="valor" {...ord} alinharDireita>Valor orçado</SortableTableHead>
                <TableHead className="w-[100px] text-right">Ações</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {isLoading ? (
                <TableRow><TableCell colSpan={9} className="text-center text-muted-foreground">Carregando...</TableCell></TableRow>
              ) : pag.itensPaginados.length === 0 ? (
                <TableRow><TableCell colSpan={9} className="text-center text-muted-foreground">
                  {filtrosAtivos > 0 ? "Nenhum projeto encontrado com esses filtros." : "Nenhum projeto cadastrado."}
                </TableCell></TableRow>
              ) : (
                pag.itensPaginados.map(p => (
                  <TableRow key={p.id} className="cursor-pointer" onClick={() => navigate(`/operacional/projetos/${p.id}`)}>
                    <TableCell className="font-medium">{p.nome}</TableCell>
                    <TableCell>{p.clienteNome ? rotuloCodigoNome(p.clienteId, p.clienteNome) : "—"}</TableCell>
                    <TableCell>{nomeUsuario(p.gerenteId)}</TableCell>
                    <TableCell>
                      <Badge variant="outline" className={STATUS_CLASSE[p.status] ?? ""}>
                        {STATUS_PROJETO[p.status] ?? p.status}
                      </Badge>
                    </TableCell>
                    <TableCell>
                      <div className="flex items-center gap-2">
                        <Progress value={Number(p.percentualConcluido)} className="h-2" />
                        <span className="w-12 text-right text-xs tabular-nums">{Number(p.percentualConcluido).toFixed(0)}%</span>
                      </div>
                    </TableCell>
                    <TableCell>{fmtData(p.dtInicioPlanejada)}</TableCell>
                    <TableCell>{fmtData(p.dtFimPlanejada)}</TableCell>
                    <TableCell className="text-right tabular-nums">{fmtMoeda(Number(p.valorOrcado))}</TableCell>
                    <TableCell className="text-right" onClick={e => e.stopPropagation()}>
                      <Button variant="ghost" size="icon" onClick={() => navigate(`/operacional/projetos/${p.id}`)}>
                        <Pencil className="h-4 w-4" />
                      </Button>
                      <Button variant="ghost" size="icon" onClick={() => setExcluirId(p.id)}>
                        <Trash2 className="h-4 w-4 text-destructive" />
                      </Button>
                    </TableCell>
                  </TableRow>
                ))
              )}
            </TableBody>
          </Table>
          <div className="mt-4">
            <PaginacaoRodape
              pagina={pag.pagina}
              totalPaginas={pag.totalPaginas}
              totalItens={pag.totalItens}
              primeiroItem={pag.primeiroItem}
              ultimoItem={pag.ultimoItem}
              tamanhoPagina={pag.tamanhoPagina}
              onPaginaChange={pag.setPagina}
              onTamanhoPaginaChange={pag.setTamanhoPagina}
            />
          </div>
        </CardContent>
      </Card>

      <AlertDialog open={excluirId != null} onOpenChange={o => !o && setExcluirId(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Excluir projeto?</AlertDialogTitle>
            <AlertDialogDescription>
              O projeto será excluído com todas as suas fases, tarefas e dependências. Esta ação não pode ser desfeita.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancelar</AlertDialogCancel>
            <AlertDialogAction onClick={confirmarExclusao}>Excluir</AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
